import {find, isEmpty, last} from 'lodash';
import {div, filler, span, vbox} from '@xh/hoist/cmp/layout';
import {markdown} from '@xh/hoist/cmp/markdown';
import {hoistCmp, HoistProps, uses} from '@xh/hoist/core';
import {banner} from '@xh/hoist/desktop/cmp/banner';
import {button} from '@xh/hoist/desktop/cmp/button';
import {textArea} from '@xh/hoist/desktop/cmp/input';
import {panel} from '@xh/hoist/desktop/cmp/panel';
import {toolbar} from '@xh/hoist/desktop/cmp/toolbar';
import {fmtDateTime, fmtNumber} from '@xh/hoist/format';
import {Icon} from '@xh/hoist/icon';
import {Classes, popover, tooltip} from '@xh/hoist/kit/blueprint';
import {AiChatModelDefinition} from '../../data/AiChatTypes';
import {aiButton, AiButtonProps, aiIcon} from '../ai/AiButton';
import {AiChatExchange, AiChatModel, AiChatStreamRound} from './AiChatModel';
import './AiChatPanel.scss';

const EXAMPLE_QUESTIONS = [
    'How are my portfolios doing today?',
    'What were my returns last year vs the S&P 500?',
    'How much in distributions did I get this year?'
];

interface ExchangeProps extends HoistProps<AiChatModel> {
    exchange: AiChatExchange;
}

type AiChatButtonProps = HoistProps<AiChatModel> & Pick<AiButtonProps, 'className' | 'icon'>;

interface RoundProps extends HoistProps {
    round: AiChatStreamRound;
    isLast: boolean;
    isActive: boolean;
}

/** App toolbar button that shows / hides the AI chat panel. */
export const aiChatButton = hoistCmp.factory<AiChatButtonProps>({
    model: uses(AiChatModel),
    render({model, ...rest}) {
        return aiButton({
            testId: 'ai-chat-btn',
            tooltip: model.isOpen ? 'Hide AI chat' : 'Ask RZX AI',
            active: model.isOpen,
            busy: model.isBusy,
            onClick: () => model.toggleOpen(),
            ...rest
        });
    }
});

/** App-wide, resizable AI chat side panel. Renders nothing while closed. */
export const aiChatPanel = hoistCmp.factory({
    model: uses(AiChatModel),
    className: 'rzx-ai-chat',
    render({model, className}) {
        if (!model.isOpen) return null;

        return panel({
            testId: 'ai-chat-panel',
            className,
            title: 'Ask RZX AI',
            icon: aiIcon(),
            compactHeader: true,
            headerItems: [
                button({
                    icon: Icon.add(),
                    tooltip: 'New conversation',
                    minimal: true,
                    disabled: isEmpty(model.exchanges) && !model.inputText,
                    onClick: () => model.newConversation()
                }),
                button({icon: Icon.close(), tooltip: 'Close', minimal: true, onClick: () => model.close()})
            ],
            modelConfig: {
                side: 'right',
                defaultSize: 420,
                minSize: 320,
                maxSize: 900,
                collapsible: false,
                persistWith: {localStorageKey: 'rzxAiChatPanel'}
            },
            item: div({
                className: 'rzx-ai-chat__transcript',
                ref: model.scrollRef,
                items: isEmpty(model.exchanges) ? emptyState({model}) : model.exchanges.map(exchange => exchangeView({model, exchange, key: exchange.id}))
            }),
            bbar: inputBar({model})
        });
    }
});

//------------------
// Model / effort picker
//------------------
const modelPicker = hoistCmp.factory<AiChatModel>(({model}) => {
    const {options, activeModelDef, effortEnabled, isBusy} = model;
    if (!options || !activeModelDef) return null;

    const activeEffort = model.effort ?? options.defaultEffort,
        defaultModelDef = find(options.models, {id: options.defaultModel}),
        price = (m: AiChatModelDefinition) => `$${m.inputUsdPerMTok} in · $${m.outputUsdPerMTok} out per M tokens`;

    // The "Default" row stores null, so the choice follows the backend default if it changes
    const rows = [
        modelRow({
            key: 'default',
            title: 'Default (recommended)',
            subtitle: defaultModelDef ? `${shortName(defaultModelDef)} · ${price(defaultModelDef)}` : null,
            selected: !model.modelId,
            onClick: () => (model.modelId = null)
        }),
        ...options.models.map(m =>
            modelRow({
                key: m.id,
                title: shortName(m),
                subtitle: price(m),
                selected: model.modelId === m.id,
                onClick: () => (model.modelId = m.id)
            })
        )
    ];

    return popover({
        interactionKind: 'click',
        placement: 'top-start',
        disabled: isBusy,
        popoverClassName: 'rzx-ai-picker-popover',
        item: button({
            className: 'rzx-ai-chat__picker',
            text: effortEnabled ? `${shortName(activeModelDef)} ${activeEffort}` : shortName(activeModelDef),
            minimal: true,
            small: true,
            disabled: isBusy
        }),
        content: div({
            className: 'rzx-ai-picker',
            items: [
                div({className: 'rzx-ai-picker__header', item: 'Select a model'}),
                div({className: 'rzx-ai-picker__list', items: rows}),
                div({
                    className: 'rzx-ai-picker__effort',
                    items: [
                        span({
                            items: ['Effort ', span({className: 'rzx-ai-picker__muted', item: effortEnabled ? `(${activeEffort})` : '(not supported)'})]
                        }),
                        filler(),
                        effortEnabled
                            ? div({
                                  className: 'rzx-ai-picker__dots',
                                  items: options.efforts.map(e =>
                                      div({
                                          key: e,
                                          title: e,
                                          className: `rzx-ai-picker__dot${e === activeEffort ? ' rzx-ai-picker__dot--active' : ''}`,
                                          // Picking the backend default stores null, as for the model
                                          onClick: () => (model.effort = e === options.defaultEffort ? null : e)
                                      })
                                  )
                              })
                            : null
                    ]
                })
            ]
        })
    });
});

interface ModelRowProps extends HoistProps {
    title: string;
    subtitle: string;
    selected: boolean;
    onClick: () => void;
}

const modelRow = hoistCmp.factory<ModelRowProps>(({title, subtitle, selected, onClick}) =>
    div({
        className: `rzx-ai-picker__row ${Classes.POPOVER_DISMISS}${selected ? ' rzx-ai-picker__row--selected' : ''}`,
        onClick,
        items: [
            vbox({
                flex: 1,
                items: [div({className: 'rzx-ai-picker__title', item: title}), subtitle ? div({className: 'rzx-ai-picker__muted', item: subtitle}) : null]
            }),
            selected ? Icon.check() : null
        ]
    })
);

/** "Claude Sonnet 5" -> "Sonnet 5": the family name is noise in a Claude-only picker. */
const shortName = (m: AiChatModelDefinition) => m.name.replace(/^Claude\s+/, '');

//------------------
// Transcript
//------------------
const emptyState = hoistCmp.factory<AiChatModel>(({model}) => {
    if (model.loadError) {
        return div({
            className: 'rzx-ai-chat__empty',
            items: [
                banner({
                    intent: 'danger',
                    // An element, not a string, so the server's error text is not parsed as markdown
                    message: span(`AI chat is unavailable: ${model.loadError}`),
                    actionButtonProps: {text: 'Retry', icon: Icon.refresh(), onClick: () => model.loadOptionsAsync()}
                })
            ]
        });
    }

    return div({
        className: 'rzx-ai-chat__empty',
        items: [
            div('Ask about your portfolios, real estate and research data.'),
            ...EXAMPLE_QUESTIONS.map(q =>
                button({
                    text: q,
                    minimal: true,
                    className: 'rzx-ai-chat__example',
                    onClick: () => model.askExampleAsync(q)
                })
            )
        ]
    });
});

const exchangeView = hoistCmp.factory<ExchangeProps>(({model, exchange}) => {
    return vbox({
        className: 'rzx-ai-chat__exchange',
        items: [div({className: 'rzx-ai-chat__question', item: exchange.question}), answerView({model, exchange})]
    });
});

const answerView = hoistCmp.factory<ExchangeProps>(({model, exchange}) => {
    const {status, response, rounds} = exchange;

    if (status === 'completed') {
        return div({
            className: 'rzx-ai-chat__answer',
            items: [markdown({content: response.answer}), detailsView({exchange})]
        });
    }

    // Streaming, or failed part way: show the rounds received so far. Text of a round that then
    // called tools is a progress note, rendered dimmed; the last round's text is the live answer.
    const items = [];
    if (status === 'pending' || (status === 'streaming' && isEmpty(rounds))) {
        items.push(div({className: 'rzx-ai-chat__progress', items: [Icon.spinner({spin: true}), span(' Thinking…')]}));
    }
    rounds.forEach(round => items.push(roundView({round, isLast: round === last(rounds), isActive: exchange.isActive})));
    if (exchange.isFailed) items.push(failureView({model, exchange}));

    return div({className: 'rzx-ai-chat__answer', items});
});

const roundView = hoistCmp.factory<RoundProps>(({round, isLast, isActive}) => {
    const isProgressNote = !isEmpty(round.tools) || !isLast,
        items = [];

    if (round.text) {
        items.push(isProgressNote ? div({className: 'rzx-ai-chat__note', item: round.text}) : markdown({content: round.text}));
    }
    round.tools.forEach((t, idx) =>
        items.push(
            div({
                key: idx,
                className: 'rzx-ai-chat__progress',
                items: [
                    t.done ? (t.isError ? Icon.warning({className: 'xh-orange'}) : Icon.check({className: 'xh-green'})) : Icon.spinner({spin: true}),
                    span(` ${t.label}${t.done ? '' : '…'}`)
                ]
            })
        )
    );
    if (isActive && isLast && !isEmpty(round.tools) && round.tools.every(t => t.done)) {
        items.push(div({className: 'rzx-ai-chat__progress', items: [Icon.spinner({spin: true}), span(' Thinking…')]}));
    }

    return div({items});
});

const failureView = hoistCmp.factory<ExchangeProps>(({model, exchange}) => {
    const {errorCode, error} = exchange,
        isCancelled = errorCode === 'cancelled';

    let message: string;
    switch (errorCode) {
        case 'cancelled':
            message = 'Cancelled.';
            break;
        case 'limit_exceeded': {
            const resetsAt = model.usage?.resetsAtUtc;
            message = `You've reached today's AI limit.${resetsAt ? ` It resets at ${fmtDateTime(resetsAt, {asHtml: true, fmt: 'MMM D, h:mma'})}.` : ''}`;
            break;
        }
        case 'rate_limited':
            message = 'The AI service is busy right now. Please try again shortly.';
            break;
        case 'invalid_request':
            message = `The request was rejected: ${error}`;
            break;
        default:
            message = `Something went wrong answering this question.${error ? ` (${error})` : ''}`;
    }

    return div({
        className: isCancelled ? 'rzx-ai-chat__cancelled' : 'rzx-ai-chat__error',
        items: [
            div(message),
            errorCode !== 'limit_exceeded'
                ? toolbar({
                      compact: true,
                      className: 'rzx-ai-chat__failure-bar',
                      items: [
                          button({text: 'Retry', icon: Icon.refresh(), minimal: true, disabled: model.isBusy, onClick: () => model.retryAsync(exchange)}),
                          button({text: 'Edit', icon: Icon.edit(), minimal: true, disabled: model.isBusy, onClick: () => model.edit(exchange)})
                      ]
                  })
                : null
        ]
    });
});

const detailsView = hoistCmp.factory<ExchangeProps>(({exchange}) => {
    const {response, showDetails} = exchange,
        {usage, toolCalls} = response,
        toggle = button({
            className: 'rzx-ai-chat__details-toggle',
            text: showDetails ? 'hide details' : 'details',
            icon: showDetails ? Icon.chevronUp() : Icon.chevronDown(),
            minimal: true,
            small: true,
            onClick: () => exchange.toggleDetails()
        });

    if (!showDetails) return toggle;

    const secs = (ms: number) => `${fmtNumber(ms / 1000, {precision: 1, asHtml: true})}s`;
    return div({
        className: 'rzx-ai-chat__details',
        items: [
            toggle,
            div(`Model: ${response.model}${response.effort ? `, effort ${response.effort}` : ''}`),
            div(`Time: ${secs(response.totalMs)} total, ${secs(usage.modelMs)} model, ${secs(usage.toolMs)} tools, ${response.rounds.length} rounds`),
            div(
                `Tokens: ${fmtNumber(usage.inputTokens, {asHtml: true})} in (${fmtNumber(usage.cacheReadInputTokens, {asHtml: true})} cached), ${fmtNumber(usage.outputTokens, {asHtml: true})} out`
            ),
            div(`Cost: $${fmtNumber(usage.costUsd, {precision: 4, asHtml: true})}`),
            isEmpty(toolCalls)
                ? div('Tools: none')
                : div({
                      items: [
                          div('Tools:'),
                          ...toolCalls.map((c, idx) =>
                              div({
                                  key: idx,
                                  className: 'rzx-ai-chat__details-tool',
                                  item: `${c.tool} (${secs(c.durationMs)})${c.result?.isError ? ' - error' : ''}`
                              })
                          )
                      ]
                  })
        ]
    });
});

//------------------
// Input
//------------------
const inputBar = hoistCmp.factory<AiChatModel>(({model}) => {
    const {isBusy, canSend, usage} = model;

    let usageHint: string = null;
    if (usage && !usage.exempt) {
        if (usage.requestsLeft != null) {
            usageHint = `${usage.requestsLeft} question${usage.requestsLeft === 1 ? '' : 's'} left today`;
        } else if (usage.costLeftUsd != null) {
            usageHint = `$${fmtNumber(usage.costLeftUsd, {precision: 2, asHtml: true})} of AI budget left today`;
        }
    }

    return vbox({
        className: 'rzx-ai-chat__input',
        items: [
            textArea({
                model,
                bind: 'inputText',
                commitOnChange: true,
                autoFocus: true,
                width: null,
                height: 64,
                placeholder: 'Ask a question… (Enter to send, Shift+Enter for a new line)',
                onKeyDown: (e: KeyboardEvent) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        model.sendAsync();
                    }
                }
            }),
            toolbar({
                compact: true,
                items: [
                    modelPicker({model}),
                    usageHint ? tooltip({compact: true, content: usageHint, item: Icon.gauge({className: 'rzx-ai-chat__usage'})}) : null,
                    filler(),
                    isBusy
                        ? button({text: 'Cancel', icon: Icon.stopCircle(), intent: 'danger', outlined: true, onClick: () => model.cancel()})
                        : button({
                              text: 'Send',
                              icon: Icon.arrowUp(),
                              intent: 'primary',
                              disabled: !canSend,
                              onClick: () => model.sendAsync()
                          })
                ]
            })
        ]
    });
});
